/**
 * DocumentUploadStep Component
 *
 * Step 3A: Upload and extract text from PDF/DOCX files.
 */

import { useState, useRef, useCallback } from 'react';
import { WizardState, WIZARD_LIMITS } from './types';

interface DocumentUploadStepProps {
  state: WizardState;
  onChange: (updates: Partial<WizardState>) => void;
  errors: string[];
}

async function extractPdf(file: File): Promise<string> {
  // Dynamic import to avoid bundling issues
  const pdfjsLib = await import('pdfjs-dist');

  // Set worker source - use CDN for simplicity
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  let text = '';

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const pageText = content.items
      .map((item) => ('str' in item ? item.str : ''))
      .join(' ');
    text += pageText + '\n';
  }

  return text.slice(0, WIZARD_LIMITS.DOCUMENT_TEXT_MAX);
}

async function extractDocx(file: File): Promise<string> {
  // Dynamic import
  const mammoth = await import('mammoth');
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value.slice(0, WIZARD_LIMITS.DOCUMENT_TEXT_MAX);
}

export function DocumentUploadStep({ state, onChange, errors }: DocumentUploadStepProps) {
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionError, setExtractionError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = useCallback(
    async (file: File) => {
      setExtractionError(null);
      setIsExtracting(true);

      try {
        // Validate file size
        if (file.size > WIZARD_LIMITS.FILE_SIZE_MAX) {
          throw new Error(
            `File too large. Maximum size is ${WIZARD_LIMITS.FILE_SIZE_MAX / 1024 / 1024}MB`
          );
        }

        // Validate file type
        const ext = file.name.split('.').pop()?.toLowerCase();
        if (!['pdf', 'docx'].includes(ext || '')) {
          throw new Error('Only PDF and DOCX files are supported');
        }

        // Extract text
        let text: string;
        if (ext === 'pdf') {
          text = await extractPdf(file);
        } else {
          text = await extractDocx(file);
        }

        // Check if extraction produced usable text
        if (!text.trim()) {
          throw new Error(
            'No text could be extracted from this file. It may be image-based or empty.'
          );
        }

        onChange({
          documentText: text,
          documentFileName: file.name,
        });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to extract text from file';
        setExtractionError(message);
        onChange({
          documentText: '',
          documentFileName: '',
        });
      } finally {
        setIsExtracting(false);
      }
    },
    [onChange]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileUpload(file);
      }
    },
    [handleFileUpload]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileUpload(file);
      }
    },
    [handleFileUpload]
  );

  const handleRemoveFile = () => {
    onChange({
      documentText: '',
      documentFileName: '',
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const previewText = state.documentText.slice(0, WIZARD_LIMITS.PREVIEW_LENGTH);
  const hasMoreText = state.documentText.length > WIZARD_LIMITS.PREVIEW_LENGTH;

  return (
    <div>
      <h2 style={{ marginBottom: 8 }}>Upload Training Materials</h2>
      <p className="text-muted" style={{ marginBottom: 24 }}>
        Upload a PDF or DOCX file containing training content. Text will be extracted automatically.
      </p>

      {(errors.length > 0 || extractionError) && (
        <div className="error-box" style={{ marginBottom: 16 }}>
          {errors.map((err, i) => (
            <div key={i}>{err}</div>
          ))}
          {extractionError && <div>{extractionError}</div>}
        </div>
      )}

      {!state.documentFileName ? (
        // Drop zone
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: isDragging
              ? '2px dashed var(--accent-primary)'
              : '2px dashed var(--border-default)',
            borderRadius: 8,
            padding: '48px 24px',
            textAlign: 'center',
            cursor: 'pointer',
            background: isDragging ? 'var(--accent-primary-light)' : 'var(--bg-muted)',
            transition: 'all 0.2s',
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />

          {isExtracting ? (
            <>
              <div style={{ fontSize: 32, marginBottom: 12 }}>...</div>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)' }}>
                Extracting text from document...
              </p>
            </>
          ) : (
            <>
              <div style={{ fontSize: 32, marginBottom: 12 }}>📄</div>
              <p style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 500 }}>
                {isDragging ? 'Drop file here' : 'Drag and drop a file here'}
              </p>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                or click to browse. Supports PDF and DOCX (max 10MB)
              </p>
            </>
          )}
        </div>
      ) : (
        // File uploaded state
        <div>
          {/* File info */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: 16,
              background: 'var(--semantic-success-light)',
              borderRadius: 8,
              marginBottom: 16,
            }}
          >
            <span style={{ fontSize: 24 }}>📄</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500, color: 'var(--semantic-success-text)' }}>
                {state.documentFileName}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {state.documentText.length.toLocaleString()} characters extracted
              </div>
            </div>
            <button
              type="button"
              onClick={handleRemoveFile}
              className="btn btn-secondary"
              style={{ fontSize: 12, padding: '4px 12px' }}
            >
              Remove
            </button>
          </div>

          {/* Preview */}
          <div className="form-group">
            <label className="label">Extracted Text Preview</label>
            <div
              style={{
                padding: 16,
                background: 'var(--bg-muted)',
                borderRadius: 8,
                fontSize: 13,
                lineHeight: 1.6,
                maxHeight: 300,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {previewText}
              {hasMoreText && (
                <span style={{ color: 'var(--text-tertiary)' }}>
                  ... [{state.documentText.length - WIZARD_LIMITS.PREVIEW_LENGTH} more characters]
                </span>
              )}
            </div>
            <p className="text-muted text-small" style={{ marginTop: 8 }}>
              This text will be used by AI to generate your assessment package.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
